import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { useApiKey } from '../contexts/ApiKeyContext';
import { 
  HelpCircle, 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Lightbulb, 
  AlertCircle,
  CheckCircle,
  Clock,
  Target,
  Activity
} from 'lucide-react';

const AgentAnalyticsDashboard = ({ onShowHelp }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [showScoringGuide, setShowScoringGuide] = useState(false);
  
  // Filter states
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTool, setSelectedTool] = useState('');
  const [selectedUseCase, setSelectedUseCase] = useState('');
  
  // Insights states
  const [showInsights, setShowInsights] = useState(false);
  const [insightsError, setInsightsError] = useState(null);
  
  // Get API key from context
  const { apiKey, error: apiKeyError } = useApiKey();
  
  // Available tools and use cases for dropdowns
  const availableTools = [
    'Agent Registration',
    'Agent Management',
    'Agent Prompt Editor',
    'Agent Self-Healing',
    'Agent Performance Tracking',
    'Agent Feedback Loop',
    'Agent Analytics',
    'CustomerFeedback'
  ];
  
  const availableUseCases = [
    'Agent Development',
    'Agent Optimization',
    'Agent Monitoring',
    'Agent Training',
    'Agent Deployment',
    'Agent Evaluation',
    'General'
  ];

  // Fetch available agents for this user
  const fetchAgents = async () => {
    try {
      const response = await axios.get(getApiUrl('/user/agents'), {
        withCredentials: true
      });
      
      if (response.data && response.data.agents) {
        setAgents(response.data.agents);
        console.log(`✅ Fetched ${response.data.count} agents for user`);
      } else {
        setAgents([]);
        console.log('✅ No agents found for user');
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setAgents([]);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      
      if (selectedAgentId) params.append('agent_id', selectedAgentId);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedTool) params.append('tool', selectedTool);
      if (selectedUseCase) params.append('use_case', selectedUseCase);
      
      // Prepare request config
      const requestConfig = {
        params,
        withCredentials: true  // Always include credentials for session auth
      };
      
      // Only add API key header if we have one
      if (apiKey) {
        requestConfig.headers = {
          'x-api-key': apiKey
        };
      }
      
      const response = await axios.get(getApiUrl('/feedback/agent/analytics'), requestConfig);
      
      setAnalyticsData(response.data);
    } catch (err) {
      console.error('Error fetching agent analytics:', err);
      setError(err.response?.data?.error || 'Failed to fetch agent analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    if (!selectedAgentId) {
      setInsightsError('Please select an agent to view insights');
      return;
    }

    setInsightsLoading(true);
    setInsightsError(null);
    
    try {
      const params = new URLSearchParams();
      params.append('agent_id', selectedAgentId);
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      // Prepare request config
      const requestConfig = {
        params,
        withCredentials: true  // Always include credentials for session auth
      };
      
      // Only add API key header if we have one
      if (apiKey) {
        requestConfig.headers = {
          'x-api-key': apiKey
        };
      }
      
      const response = await axios.get(getApiUrl('/agent/feedback/insights'), requestConfig);
      
      setInsightsData(response.data);
      setShowInsights(true);
    } catch (err) {
      console.error('Error fetching agent insights:', err);
      setInsightsError(err.response?.data?.error || 'Failed to fetch agent insights');
    } finally {
      setInsightsLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedAgentId]);

  const handleApplyFilters = () => {
    fetchAnalytics();
    if (showInsights && selectedAgentId) {
      fetchInsights();
    }
  };

  const handleClearFilters = () => {
    setSelectedAgentId('');
    setStartDate('');
    setEndDate('');
    setSelectedTool('');
    setSelectedUseCase('');
    setShowInsights(false);
    setInsightsData(null);
    fetchAnalytics();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };



  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';  // Excellent (adjusted for modern LLMs)
    if (score >= 75) return 'text-yellow-600'; // Good (adjusted for modern LLMs)
    return 'text-red-600';                      // Poor (below 75)
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'declining':
        return <TrendingDown className="w-5 h-5 text-red-500" />;
      default:
        return <Minus className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'improving':
        return 'text-green-500';
      case 'declining':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getTrendText = (trend) => {
    switch (trend) {
      case 'improving':
        return 'Improving';
      case 'declining':
        return 'Declining';
      default:
        return 'Steady';
    }
  };

  // Interactive Bar Chart for Score Over Time
  const InteractiveBarChart = ({ data, title }) => {
    const [hoveredBar, setHoveredBar] = useState(null);

    // Debug logging
    console.log('InteractiveBarChart data:', data);

    if (!data || data.length === 0) {
      return (
        <div className="bg-gray-800 rounded-xl p-6 text-center border border-gray-600">
          <p className="text-gray-400">No data available for {title}</p>
        </div>
      );
    }

    const maxScore = Math.max(...data.map(d => d.average_score));
    const minScore = Math.min(...data.map(d => d.average_score));
    const scoreRange = maxScore - minScore;

    return (
      <div className="bg-gray-800 rounded-xl shadow p-6 border border-gray-600" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.2)' }}>
        <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
        
        {/* Chart Container */}
        <div className="relative">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-xs text-gray-400">
            <span>{maxScore.toFixed(1)}</span>
            <span>{((maxScore + minScore) / 2).toFixed(1)}</span>
            <span>{minScore.toFixed(1)}</span>
          </div>

          {/* Bars Container */}
          <div className="ml-12 h-48 flex items-end space-x-2">
            {data.map((item, index) => {
              const score = item.average_score;
              // Ensure minimum height and handle identical scores
              let height;
              if (scoreRange > 0) {
                height = ((score - minScore) / scoreRange) * 100;
              } else {
                // If all scores are identical, use a default height
                height = 60; // 60% height for identical scores
              }
              // Ensure minimum height for visibility
              height = Math.max(height, 10); // At least 10% height
              
              const isImproving = index > 0 ? score > data[index - 1].average_score : null;
              const isDeclining = index > 0 ? score < data[index - 1].average_score : null;
              
              return (
                <div key={index} className="flex-1 flex flex-col items-center">
                  {/* Bar */}
                  <div 
                    className={`w-full rounded-t transition-all duration-300 cursor-pointer relative group ${
                      hoveredBar === index 
                        ? 'ring-2 ring-purple-400 ring-opacity-50' 
                        : ''
                    }`}
                    style={{ 
                      height: `${height}%`,
                      backgroundColor: isImproving 
                        ? '#10B981' 
                        : isDeclining 
                        ? '#EF4444' 
                        : scoreRange === 0 
                        ? '#8B5CF6' // Purple for steady performance when all scores are identical
                        : '#7B61FF',
                      minHeight: '4px' // Ensure minimum visible height
                    }}
                    onMouseEnter={() => setHoveredBar(index)}
                    onMouseLeave={() => setHoveredBar(null)}
                  >
                    {/* Hover Tooltip */}
                    {hoveredBar === index && (
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg border border-gray-600 z-10">
                        <div className="font-medium">{formatDate(item.date)}</div>
                        <div className={`${getScoreColor(score)}`}>
                          Score: {score.toFixed(1)}
                        </div>
                        {isImproving && (
                          <div className="text-green-400 flex items-center">
                            <TrendingUp className="w-3 h-3 mr-1" />
                            Improving
                          </div>
                        )}
                        {isDeclining && (
                          <div className="text-red-400 flex items-center">
                            <TrendingDown className="w-3 h-3 mr-1" />
                            Declining
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* X-axis label */}
                  <div className="text-xs text-gray-400 mt-2 text-center">
                    {formatDate(item.date)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Trend Indicator */}
          <div className="mt-4 text-center">
            <div className="inline-flex items-center space-x-2 text-sm">
              <span className="text-gray-400">Trend:</span>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-green-400">Improving</span>
                <div className="w-3 h-3 rounded-full bg-red-500 ml-2"></div>
                <span className="text-red-400">Declining</span>
                <div className="w-3 h-3 rounded-full bg-purple-500 ml-2"></div>
                <span className="text-purple-400">Steady</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Simple line chart for other uses
  const LineChart = ({ data, title }) => {
    if (!data || data.length === 0) {
      return (
        <div className="bg-gray-800 rounded-xl p-6 text-center border border-gray-600">
          <p className="text-gray-400">No data available for {title}</p>
        </div>
      );
    }

    const maxScore = Math.max(...data.map(d => d.average_score));
    const minScore = Math.min(...data.map(d => d.average_score));

    return (
      <div className="bg-gray-800 rounded-xl shadow p-6 border border-gray-600" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.2)' }}>
        <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
        <div className="space-y-2">
          {data.map((item, index) => {
            const score = item.average_score;
            const percentage = maxScore > minScore ? ((score - minScore) / (maxScore - minScore)) * 100 : 50;
            return (
              <div key={index} className="flex items-center space-x-4">
                <div className="w-20 text-sm text-gray-400">
                  {formatDate(item.date)}
                </div>
                <div className="flex-1">
                  <div className="bg-gray-700 rounded-full h-4">
                    <div 
                      className="bg-[#7B61FF] h-4 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className={`w-16 text-sm font-medium ${getScoreColor(score)}`}>
                  {score.toFixed(1)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const PieChart = ({ data, title }) => {
    if (!data || data.length === 0) {
      return (
        <div className="bg-gray-800 rounded-xl p-6 text-center border border-gray-600">
          <p className="text-gray-400">No data available for {title}</p>
        </div>
      );
    }

    const total = data.reduce((sum, item) => sum + item.count, 0);
    const colors = ['bg-[#7B61FF]', 'bg-[#00CFFF]', 'bg-yellow-500', 'bg-red-500', 'bg-purple-500', 'bg-pink-500'];

    return (
      <div className="bg-gray-800 rounded-xl shadow p-6 border border-gray-600" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.2)' }}>
        <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
        <div className="space-y-3">
          {data.map((item, index) => {
            const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
            return (
              <div key={index} className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded-full ${colors[index % colors.length]}`}></div>
                <div className="flex-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-300">{item.tool || item.issue}</span>
                    <span className="font-medium text-white">{item.count}</span>
                  </div>
                  <div className="bg-gray-700 rounded-full h-2 mt-1">
                    <div 
                      className={`${colors[index % colors.length]} h-2 rounded-full transition-all duration-300`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="w-12 text-xs text-gray-400 text-right">
                  {percentage}%
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Insights Component
  const AgentInsights = ({ insights }) => {
    if (!insights) return null;

    return (
      <div className="bg-gray-800 rounded-xl shadow p-6 border border-gray-600 mb-8" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.2)' }}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white flex items-center">
            <Lightbulb className="w-6 h-6 mr-2 text-yellow-500" />
            Agent Insights & Recommendations
          </h2>
          <div className="flex items-center space-x-2">
            {getTrendIcon(insights.score_trend)}
            <span className={`font-medium ${getTrendColor(insights.score_trend)}`}>
              {getTrendText(insights.score_trend)}
            </span>
          </div>
        </div>

        {/* Performance Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex items-center">
              <Target className="w-5 h-5 text-blue-400 mr-2" />
              <span className="text-gray-300 text-sm">Average Score</span>
            </div>
            <p className={`text-2xl font-bold ${getScoreColor(insights.average_score)}`}>
              {insights.average_score.toFixed(1)}
            </p>
          </div>
          
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex items-center">
              <Activity className="w-5 h-5 text-green-400 mr-2" />
              <span className="text-gray-300 text-sm">Feedback Count</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {insights.feedback_count}
            </p>
          </div>
          
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex items-center">
              <Clock className="w-5 h-5 text-purple-400 mr-2" />
              <span className="text-gray-300 text-sm">Last Updated</span>
            </div>
            <p className="text-sm text-gray-300">
              {formatDate(insights.last_updated)}
            </p>
          </div>
        </div>

        {/* Recent Issues */}
        {insights.recent_issues && insights.recent_issues.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
              <AlertCircle className="w-5 h-5 text-orange-400 mr-2" />
              Recent Issues
            </h3>
            <div className="space-y-2">
              {insights.recent_issues.map((issue, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-700 rounded-lg p-3">
                  <span className="text-gray-300">{issue.issue}</span>
                  <span className="text-white font-medium">{issue.count} occurrences</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Suggested Actions */}
        {insights.suggested_actions && insights.suggested_actions.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
              <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
              Recommended Actions
            </h3>
            <div className="space-y-3">
              {insights.suggested_actions.map((action, index) => (
                <div key={index} className="flex items-start space-x-3 bg-gray-700 rounded-lg p-4">
                  <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300 leading-relaxed">{action}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Check for valid API key
  const [validApiKeys, setValidApiKeys] = useState([]);

  useEffect(() => {
    const fetchValidApiKeys = async () => {
      try {
        const apiUrl = getApiUrl('/valid-api-keys');
        console.log('Fetching valid API keys from URL:', apiUrl);

        // Force Vercel deployment - updated API call with credentials
        const response = await axios.get(apiUrl, { withCredentials: true });
        console.log('Valid API keys fetched successfully:', response.data.keys);
        setValidApiKeys(response.data.keys || []);
      } catch (err) {
        console.error('Error fetching valid API keys:', err);
        console.error('Error response data:', err.response?.data);
        if (err.response?.status === 404) {
          console.warn('The valid API keys endpoint returned 404. Skipping to user-specific validation.');
          const userApiUrl = getApiUrl('/user/api-keys');
          console.log('Fetching user-specific API keys from URL:', userApiUrl);
          try {
            const userResponse = await axios.get(userApiUrl, { withCredentials: true });
            console.log('User-specific API keys fetched successfully:', userResponse.data.keys);
            setValidApiKeys(userResponse.data.keys || []);
          } catch (userErr) {
            console.error('Error fetching user-specific API keys:', userErr);
          }
        } else {
          console.error('Unexpected error while fetching valid API keys:', err);
        }
      }
    };

    fetchValidApiKeys();
  }, []);

  if (!apiKey || !validApiKeys.includes(apiKey)) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
          <h1 className="text-3xl font-bold text-white mb-6">Agent Performance Dashboard</h1>
          <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6">
            <h3 className="font-semibold text-red-300 mb-2">API Key Required</h3>
            <p className="text-red-300">
              {apiKeyError || 'API key is required to access this dashboard. Please contact your admin.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !analyticsData) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7B61FF]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Agent Performance Dashboard</h1>
            <p className="text-gray-400">Track and analyze the performance of your AI agents over time with actionable insights</p>
          </div>
          {onShowHelp && (
            <button
              onClick={onShowHelp}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-300"
            >
              <HelpCircle className="w-4 h-4" />
              <span>Help</span>
            </button>
          )}
        </div>

        {/* Scoring System Guide */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-600 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-white flex items-center">
              <Target className="w-5 h-5 mr-2 text-purple-400" />
              Scoring System & Self-Healing Guide
            </h3>
            <button
              onClick={() => setShowScoringGuide(!showScoringGuide)}
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              {showScoringGuide ? 'Hide' : 'Show'} Details
            </button>
          </div>
          
          {showScoringGuide && (
            <div className="space-y-4 text-sm">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Scoring System */}
                <div>
                  <h4 className="font-semibold text-purple-400 mb-3">📊 Quality Scoring System</h4>
                  <div className="space-y-2 text-gray-300">
                    <div className="flex items-center justify-between">
                      <span>85-100</span>
                      <span className="text-green-400 font-medium">Excellent</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>75-84</span>
                      <span className="text-yellow-400 font-medium">Good</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>60-74</span>
                      <span className="text-orange-400 font-medium">Poor</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>0-59</span>
                      <span className="text-red-400 font-medium">Critical</span>
                    </div>
                  </div>
                  <p className="text-gray-400 mt-3 text-xs">
                    Scores are evaluated by Claude AI based on accuracy, relevance, and quality.
                  </p>
                </div>
                
                {/* Self-Healing Triggers */}
                <div>
                  <h4 className="font-semibold text-blue-400 mb-3">🔧 Self-Healing Triggers</h4>
                  <div className="space-y-2 text-gray-300">
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-red-400">Critical (Score &lt; 60):</span>
                        <p className="text-xs text-gray-400">Immediate action required. Generates prompt updates and alerts.</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-orange-400">Poor (Score &lt; 75):</span>
                        <p className="text-xs text-gray-400">Performance below threshold. Generates improvement suggestions.</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-yellow-400">Declining Trend:</span>
                        <p className="text-xs text-gray-400">Performance getting worse. Triggers investigation.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* When to Use */}
              <div className="border-t border-gray-600 pt-4">
                <h4 className="font-semibold text-emerald-400 mb-3">🎯 When to Use Self-Healing</h4>
                <div className="grid md:grid-cols-2 gap-4 text-xs text-gray-300">
                  <div>
                    <p className="font-medium text-emerald-400 mb-2">✅ Appropriate for:</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Agents with consistent poor performance</li>
                      <li>Performance that's declining over time</li>
                      <li>Agents with specific, recurring issues</li>
                      <li>When you want AI-suggested improvements</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-red-400 mb-2">❌ Not appropriate for:</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Agents performing excellently (85+)</li>
                      <li>One-time performance issues</li>
                      <li>Agents with insufficient feedback data</li>
                      <li>When you prefer manual prompt editing</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-gray-800 rounded-xl p-6 mb-8 border border-gray-600">
          <h2 className="text-lg font-semibold text-white mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Agent Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Agent
              </label>
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
              >
                <option value="">All Agents</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
              />
            </div>

            {/* Tool Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tool
              </label>
              <select
                value={selectedTool}
                onChange={(e) => setSelectedTool(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
              >
                <option value="">All Tools</option>
                {availableTools.map((tool) => (
                  <option key={tool} value={tool}>
                    {tool}
                  </option>
                ))}
              </select>
            </div>

            {/* Use Case Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Use Case
              </label>
              <select
                value={selectedUseCase}
                onChange={(e) => setSelectedUseCase(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
              >
                <option value="">All Use Cases</option>
                {availableUseCases.map((useCase) => (
                  <option key={useCase} value={useCase}>
                    {useCase}
                  </option>
                ))}
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2 items-end">
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Apply Filters
              </button>
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Insights Button */}
          {selectedAgentId && (
            <div className="mt-4 pt-4 border-t border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  <span className="text-gray-300">Agent Insights</span>
                </div>
                <button
                  onClick={fetchInsights}
                  disabled={insightsLoading}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 flex items-center space-x-2"
                >
                  {insightsLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <Lightbulb className="w-4 h-4" />
                      <span>{showInsights ? 'Refresh Insights' : 'Get Insights'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-red-300">{error}</span>
            </div>
          </div>
        )}

        {/* Insights Error Display */}
        {insightsError && (
          <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-red-300">{insightsError}</span>
            </div>
          </div>
        )}

        {/* Agent Insights Section */}
        {showInsights && insightsData && (
          <AgentInsights insights={insightsData} />
        )}

        {/* Analytics Data */}
        {analyticsData && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gray-800 rounded-lg shadow p-6 border-l-4 border-blue-500">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Average Score</p>
                    <p className={`text-2xl font-bold ${getScoreColor(analyticsData.average_score)}`}>
                      {analyticsData.average_score.toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg shadow p-6 border-l-4 border-green-500">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Total Logs</p>
                    <p className="text-2xl font-bold text-white">
                      {analyticsData.total_logs}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg shadow p-6 border-l-4 border-blue-500">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Average Score</p>
                    <p className="text-2xl font-bold text-white">
                      {analyticsData.average_score?.toFixed(1) || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg shadow p-6 border-l-4 border-green-500">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Performance Trend</p>
                    <p className="text-2xl font-bold text-white">
                      {analyticsData.trend || 'Steady'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Score Over Time */}
              <InteractiveBarChart 
                data={analyticsData.score_over_time} 
                title="Score Over Time" 
              />

              {/* Issue Breakdown */}
              <PieChart 
                data={analyticsData.issue_breakdown} 
                title="Issue Breakdown" 
              />

              {/* Tool Usage */}
              <PieChart 
                data={analyticsData.tool_usage} 
                title="Tool Usage" 
              />

              {/* Performance Summary */}
              <div className="bg-gray-800 rounded-lg shadow p-6 border border-gray-600">
                <h3 className="text-lg font-semibold text-white mb-4">Performance Summary</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">
                        {analyticsData.average_score?.toFixed(1) || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-400">Average Score</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">
                        {analyticsData.total_logs || 0}
                      </div>
                      <div className="text-sm text-gray-400">Total Evaluations</div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="inline-flex items-center space-x-2 text-sm">
                      <span className="text-gray-400">Trend:</span>
                      <span className={`font-medium ${getTrendColor(analyticsData.trend)}`}>
                        {getTrendText(analyticsData.trend)}
                      </span>
                      {getTrendIcon(analyticsData.trend)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* No Data State */}
        {!loading && !analyticsData && !error && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2v-14a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-400">No data available</h3>
            <p className="mt-1 text-sm text-gray-500">Try adjusting your filters or select a different agent.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentAnalyticsDashboard;